/** Admin/tech lookups: raw WebAuthn values → product names. */

export const AAGUID_CATALOG = {
  '00000000-0000-0000-0000-000000000000': {
    name: 'Not provided',
    vendor: '—',
    note: 'All zeros. Common with attestation=none. Apple often does this for iCloud Keychain.',
  },
  'ea9b8d66-4d01-1d21-3ce4-b6b48cb575d4': {
    name: 'Google Password Manager',
    vendor: 'Google',
    note: 'Synced Google account vault.',
  },
  'fbfc3007-154e-4ecc-8c0b-6e020557d7bd': {
    name: 'iCloud Keychain / Apple Passwords',
    vendor: 'Apple',
    note: 'Synced Apple platform passkey.',
  },
  'dd4ec289-e01d-41c9-bb89-70fa845d4bf2': {
    name: 'iCloud Keychain (managed / variant)',
    vendor: 'Apple',
    note: 'Apple platform passkey (managed Apple ID variant).',
  },
  'adce0002-35bc-c60a-648b-0b25f1f05503': {
    name: 'Chrome on Mac (legacy platform)',
    vendor: 'Google',
    note: 'Older Chrome-on-Mac platform authenticator.',
  },
  '08987058-cadc-4b81-b6e1-30de50dcbe96': {
    name: 'Windows Hello',
    vendor: 'Microsoft',
    note: 'Device-bound (software / VBS Hello).',
  },
  '6028b017-b1d4-4c02-b4b3-afcdafc96bb2': {
    name: 'Windows Hello',
    vendor: 'Microsoft',
    note: 'Device-bound Windows Hello.',
  },
  '9ddd1817-af5a-4672-a2b9-3e3dd95000a9': {
    name: 'Windows Hello (hardware)',
    vendor: 'Microsoft',
    note: 'TPM / hardware-backed Hello.',
  },
  'bada5566-a7aa-401f-bd96-45619a55120d': {
    name: '1Password',
    vendor: '1Password',
    note: 'Synced 1Password vault.',
  },
  'd548826e-79b4-db40-a3d8-11116f7e8349': {
    name: 'Bitwarden',
    vendor: 'Bitwarden',
    note: 'Synced Bitwarden vault.',
  },
  'd548826e-5796-f4cd-4f63-e2b0aa3a5c6d': {
    name: 'Bitwarden (alternate AAGUID)',
    vendor: 'Bitwarden',
    note: 'Synced Bitwarden vault.',
  },
  '531126d6-e717-415c-9320-3d9aa6981239': {
    name: 'Dashlane',
    vendor: 'Dashlane',
    note: 'Synced Dashlane vault.',
  },
};

export function lookupAaguid(uuid) {
  if (!uuid) {
    return { name: 'Missing', vendor: '—', note: 'No AAGUID in the registration result.' };
  }
  const key = String(uuid).toLowerCase();
  return AAGUID_CATALOG[key] || {
    name: 'Unknown authenticator',
    vendor: 'Not in catalog',
    note: 'No product mapping for this AAGUID. Treat the raw value as the identifier.',
  };
}

export function meaningBackupEligible(value) {
  if (value === true) return 'Yes — the private key can leave this device (synced / backup-eligible).';
  if (value === false) return 'No — device-bound; the private key is not eligible to sync.';
  return 'Not reported.';
}

export function meaningBackedUp(value) {
  if (value === true) return 'Yes — the authenticator says a backup/sync copy already exists.';
  if (value === false) return 'No — not currently backed up (may still be eligible).';
  return 'Not reported.';
}

export function meaningStoreClass(value) {
  if (value === 'synced_vault') return 'Synced vault (password manager / iCloud / GPM).';
  if (value === 'hardware_bound') return 'Hardware-bound (TPM, security key, or non-exportable platform key).';
  return 'Could not classify the store.';
}

export function meaningAttachment(value) {
  if (value === 'platform') return 'This device (platform authenticator).';
  if (value === 'cross-platform') return 'Roaming: security key or phone.';
  if (!value) return 'Not reported.';
  return String(value);
}

export function meaningAlgorithm(value) {
  const map = {
    ES256: 'ECDSA P-256 (typical for Apple / Android)',
    RS256: 'RSASSA-PKCS1-v1_5 + SHA-256 (typical for Windows Hello)',
    EdDSA: 'Ed25519',
    ES384: 'ECDSA P-384',
    RS1: 'RSASSA-PKCS1-v1_5 + SHA-1 (legacy)',
  };
  return map[value] || value || 'Not reported.';
}

export function meaningMethod(method) {
  const map = {
    aaguid_match: 'Catalog hit on AAGUID — product name is from the lookup table.',
    inferred_safari_platform: 'Safari cannot use GPM as the platform store; treated as iCloud Keychain.',
    inferred_apple_zero_aaguid: 'AAGUID is all zeros on Apple. Inferred iCloud Keychain (not proven).',
    guessed_chrome_windows_synced: 'Guess: Chrome on Windows + backup-eligible, no AAGUID match → likely Google Password Manager.',
    unknown: 'No catalog hit and not enough platform context to infer.',
  };
  return map[method] || method || 'Not reported.';
}

export function meaningUv(value) {
  if (value === true) return 'User verification bit set (PIN / biometrics satisfied).';
  if (value === false) return 'User verification bit not set.';
  return 'Not reported.';
}

export function meaningDiscoverable(value) {
  if (value === true) return 'Discoverable / resident key (a passkey).';
  if (value === false) return 'Non-discoverable (server must send allowCredentials).';
  return 'Not reported (credProps.rk missing).';
}
